import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { FiCalendar, FiArchive, FiSettings, FiPlus } from "react-icons/fi"
import { TbSquareLetterP, TbSquareLetterM } from "react-icons/tb"

import "./index.scss"

import { Popup, ListItemDnD, ConfirmDialog } from 'components'
import { MemberConfig } from 'components/Config'
import { members as membersState, teamInfo, projects, limsCapabilities } from "state_management"
import dragTypes from "utils/scripts/dragTypes"
import useT from "utils/hooks/useT"

export default function MembersConfig() {
  const [addConfig, setAddConfig] = useState(false)
  const [activeEdit, setActiveEdit] = useState(null)
  const [activeArchive, setActiveArchive] = useState(null)

  const members = Object.values(useSelector((state) => state.members))
  const dispatch = useDispatch()
  const { t } = useT("common")
  const hasFetched = useSelector((state) => state.teamInfo.hasFetched)
  // SPEC-057 L1 — the archive authorisation matrix (FE mirror; the backend enforces it and
  // answers 409 MEMBER_HOLDS_QA_ROLE / 403 as fallback).
  const authRole = useSelector((state) => state.auth.role)
  const authMemberId = useSelector((state) => state.auth.memberId)
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const actorIsQaManager = useSelector(limsCapabilities.selectIsLimsQaManager)

  useEffect(() => {
    if (!hasFetched) {
      dispatch(teamInfo.fetch())
    }
  }, [hasFetched, dispatch])

  const handleArchive = (member) => {
    setActiveArchive(member)
  }

  const confirmArchive = () => {
    if (activeArchive) {
      dispatch(membersState.archive(activeArchive.id))
        .then(() => dispatch(projects.fetch()))
        // The toast (incl. the translated 409 MEMBER_HOLDS_QA_ROLE fallback) comes from the
        // message layer — just close the dialog either way.
        .catch(() => {})
        .finally(() => setActiveArchive(null))
    }
  }

  const handleEdit = (member) => {
    setActiveEdit(member)
  }

  const handleAdd = () => {
    setAddConfig(true)
  }

  const handleDrop = (dragId, dropId) => {
    const dropIdx = members.findIndex((x) => x.id === dropId)
    const dragIdx = members.findIndex((x) => x.id === dragId)
    if (dragIdx > dropIdx) {
      members.splice(dropIdx, 0, members[dragIdx])
      members.splice(dragIdx + 1, 1)
    } else {
      members.splice(dropIdx + 1, 0, members[dragIdx])
      members.splice(dragIdx, 1)
    }
    const ids = members.map((x) => x.id)
    dispatch(membersState.editOrder({ ids: ids }))
  }

  return (
    <div className="MembersConfig-main-container">
      {activeArchive && (
        <ConfirmDialog
          onCancel={() => setActiveArchive(null)}
          onConfirm={confirmArchive}
        >
          <h4>{t("confirmArchivalOfMember", { initials: activeArchive.initials })}</h4>
        </ConfirmDialog>
      )}
      {activeEdit && (
        <Popup nonScrollable onCancel={() => setActiveEdit(null)}>
          <MemberConfig member={activeEdit} onClose={() => setActiveEdit(null)} />
        </Popup>
      )}
      {addConfig && (
        <Popup nonScrollable onCancel={() => setAddConfig(false)}>
          <MemberConfig onClose={() => setAddConfig(false)} />
        </Popup>
      )}
      <div className="MembersConfig-member-container">
        {members
          .filter((member) => !member.archived)
          .map((member) => {
            const archiveState = limsCapabilities.archiveDecision({
              actorRole: authRole,
              actorIsQaManager,
              actorMemberId: authMemberId,
              showLims,
              target: member,
            })
            return (
            <ListItemDnD
              text={member.initials}
              key={member.id}
              dragType={dragTypes.MEMBER}
              id={member.id}
              item={member}
              onDrop={handleDrop}
            >
              {member.outlookLink && (
                <FiCalendar
                  className="MembersConfig-member-outlook-icon"
                  title={t("memberHasEnabledOutlook")}
                />
              )}
              {member.role === "MEMBER" ? (
                <TbSquareLetterM title={t("member")} />
              ) : (
                <TbSquareLetterP title={t("planner")} />
              )}
              {/* SPEC-056 UI-review: the QA qualifications are independent of the planning
                  role — show them as their own badges (LIMS teams only). */}
              {showLims && (member.qcCapabilities || []).includes("qa-manager") && (
                <span className="MembersConfig-qa-badge" title={t("limsCapQaManager")}>QM</span>
              )}
              {showLims &&
                (member.qcCapabilities || []).includes("qa") &&
                !(member.qcCapabilities || []).includes("qa-manager") && (
                  <span className="MembersConfig-qa-badge" title={t("limsCapQa")}>QA</span>
                )}
              {archiveState.allowed && (
                <FiArchive
                  onClick={() => handleArchive(member)}
                  title={t("archiveMember")}
                />
              )}
              {!archiveState.allowed && archiveState.tooltipKey && (
                <FiArchive
                  className="MembersConfig-archive-disabled"
                  title={t(archiveState.tooltipKey)}
                />
              )}
              <FiSettings
                onClick={() => handleEdit(member)}
                title={t("editMember")}
              />
            </ListItemDnD>
            )
          })}
        <div className="MembersConfig-add-member">
          <FiPlus size={40} title={t("addMember")} onClick={handleAdd} />
        </div>
      </div>
    </div>
  )
}
